<template>
	<SuiDataTableRow>
		<SuiDataTableCell
			v-for="item in totalRow"
			:key="item.id"
			class="project-profit-table-totals-row__totals"
		>
			<div
				class="project-profit-table-totals-row__cell"
				:data-testid="`total-row-cell-${item.id}`"
				:class="item.class"
			>
				<strong>
					{{ item.total }}
				</strong>
				<span
					v-if="
						item.id ===
							ProjectProfitTableColumnTypes.TO_RECOGNIZE ||
						item.id === ProjectProfitTableColumnTypes.TO_INVOICE
					"
				/>
				<span
					v-else
					class="project-profit-table-totals-row__percentage"
				>
					{{ item.percentage }}
				</span>
			</div>
		</SuiDataTableCell>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import { SuiDataTableCell, SuiDataTableRow } from '@developers/scoro-ui';
import { computed, toRefs } from 'vue';
import { useFormatter, useTranslation } from '@/composables';
import { ProjectProfitTableColumnTypes } from './types';
const { t } = useTranslation();

export interface ProjectProfitTableTotalsRowProps {
	revenueBudgeted: number;
	revenueEarned: number;
	revenueInvoiced: number;
	revenueRecognized: number;
	revenueForecasted: number;
	costBudgeted: number;
	costInvoiced: number;
	costRecognized: number;
	costForecasted: number;
	isAnyBudgetSet: boolean;
	canSeeRecognized: boolean;
	canSeeForecast: boolean;
	isRevenueTmModel: boolean;
	isRevenueTmNotCapped: boolean;
}

const props = defineProps<ProjectProfitTableTotalsRowProps>();

const {
	revenueBudgeted,
	revenueEarned,
	revenueRecognized,
	revenueInvoiced,
	revenueForecasted,
	costBudgeted,
	costRecognized,
	costInvoiced,
	costForecasted,
	isAnyBudgetSet,
	canSeeRecognized,
	canSeeForecast,
	isRevenueTmModel,
	isRevenueTmNotCapped,
} = toRefs(props);

const { formatNumber } = useFormatter();

const revenueForecastedTotal = computed(
	() => revenueForecasted.value + revenueRecognized.value
);
const costForecastedTotal = computed(
	() => costForecasted.value + costRecognized.value
);

const totalBudgetedProfit = computed(
	() => revenueBudgeted.value - costBudgeted.value
);

const totalBudgetedProfitPercentage = computed(() =>
	revenueBudgeted.value === 0
		? undefined
		: (totalBudgetedProfit.value / revenueBudgeted.value) * 100
);

const totalEarnedProfit = computed(
	() => revenueEarned.value - costRecognized.value
);

const totalEarnedProfitPercentage = computed(() =>
	revenueEarned.value === 0
		? undefined
		: (totalEarnedProfit.value / revenueEarned.value) * 100
);

const totalRecognizedProfit = computed(
	() => revenueRecognized.value - costRecognized.value
);

const totalRecognizedProfitPercentage = computed(() =>
	revenueRecognized.value === 0
		? undefined
		: (totalRecognizedProfit.value / revenueRecognized.value) * 100
);

const totalInvoicedProfit = computed(
	() => revenueInvoiced.value - costInvoiced.value
);

const totalInvoicedProfitPercentage = computed(() =>
	revenueInvoiced.value === 0
		? undefined
		: (totalInvoicedProfit.value / revenueInvoiced.value) * 100
);

const totalForecastedProfit = computed(
	() => revenueForecastedTotal.value - costForecastedTotal.value
);

const totalForecastedProfitPercentage = computed(() =>
	revenueForecastedTotal.value === 0
		? undefined
		: (totalForecastedProfit.value / revenueForecastedTotal.value) * 100
);

const getTotalBudgetedProfitCellClass = (total: number) => {
	if (total < 0) {
		return 'project-profit-table-totals-row__cell--negative';
	}
};

const getTotalRowValueClass = (
	actualPercentage?: number,
	expectedPercentage?: number
) => {
	if (
		actualPercentage === undefined ||
		expectedPercentage === undefined ||
		actualPercentage === expectedPercentage
	) {
		return '';
	}

	return `project-profit-table-totals-row__cell--${
		actualPercentage > expectedPercentage ? 'positive' : 'negative'
	}`;
};

const totalRow = computed(() => {
	return [
		{
			id: ProjectProfitTableColumnTypes.BUDGETED,
			total: formatNumber(totalBudgetedProfit.value),
			percentage:
				totalBudgetedProfitPercentage.value === undefined
					? t('n_a')
					: `${formatNumber(totalBudgetedProfitPercentage.value)}%`,
			class: getTotalBudgetedProfitCellClass(totalBudgetedProfit.value),
		},
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.EARNED,
						total: formatNumber(totalEarnedProfit.value),
						percentage:
							totalEarnedProfitPercentage.value === undefined
								? t('n_a')
								: `${formatNumber(totalEarnedProfitPercentage.value)}%`,
						class: getTotalRowValueClass(
							totalEarnedProfitPercentage.value,
							isRevenueTmNotCapped.value
								? undefined
								: totalBudgetedProfitPercentage.value
						),
					},
				]
			: []),
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.RECOGNIZED_TO_DATE,
						total: formatNumber(totalRecognizedProfit.value),
						percentage:
							totalRecognizedProfitPercentage.value === undefined
								? t('n_a')
								: `${formatNumber(
										totalRecognizedProfitPercentage.value
									)}%`,
						class: getTotalRowValueClass(
							totalRecognizedProfitPercentage.value,
							isRevenueTmNotCapped.value
								? undefined
								: totalBudgetedProfitPercentage.value
						),
					},
				]
			: []),
		...(canSeeRecognized.value &&
		!isRevenueTmModel.value &&
		isAnyBudgetSet.value
			? [
					{
						id: ProjectProfitTableColumnTypes.TO_RECOGNIZE,
					},
				]
			: []),
		{
			id: ProjectProfitTableColumnTypes.INVOICED_TO_DATE,
			total: formatNumber(totalInvoicedProfit.value),
			percentage:
				totalInvoicedProfitPercentage.value === undefined
					? t('n_a')
					: `${formatNumber(totalInvoicedProfitPercentage.value)}%`,
			class: getTotalRowValueClass(
				totalInvoicedProfitPercentage.value,
				isRevenueTmNotCapped.value
					? undefined
					: totalBudgetedProfitPercentage.value
			),
		},
		{
			id: ProjectProfitTableColumnTypes.TO_INVOICE,
		},
		...(canSeeForecast.value
			? [
					{
						id: ProjectProfitTableColumnTypes.FORECASTED,
						total: formatNumber(totalForecastedProfit.value),
						percentage:
							totalForecastedProfitPercentage.value === undefined
								? t('n_a')
								: `${formatNumber(
										totalForecastedProfitPercentage.value
									)}%`,
						class: getTotalRowValueClass(
							totalForecastedProfitPercentage.value,
							isRevenueTmNotCapped.value
								? undefined
								: totalBudgetedProfitPercentage.value
						),
					},
				]
			: []),
	];
});
</script>

<style scoped lang="scss">
.sui-data-table-col:last-child {
	padding-right: 1.5rem !important;
}

.project-profit-table-totals-row {
	&__totals {
		--sui-data-table-cell-padding: 2rem;

		text-align: right;
		text-wrap: nowrap;
	}

	&__cell {
		display: flex;
		flex-direction: column;
		color: $neutral-90;

		&--negative {
			color: $red-50;
		}

		&--positive {
			color: $green-50;
		}
	}

	&__percentage {
		font-size: 1.5rem;
		font-weight: $scoro-fw-normal;
	}
}
</style>
